import WebApp from '@twa-dev/sdk';

/**
 * Wrapper tipis di sekitar Telegram WebApp SDK. Dua tujuan:
 *   1. UI code tidak perlu tahu detail SDK — bila SDK berubah, hanya
 *      file ini yg berubah.
 *   2. `initData` yg dipakai untuk auth backend selalu diambil dari
 *      SDK, bukan URL query — mencegah spoofing lewat parameter.
 *
 * Bila aplikasi dibuka di browser biasa (bukan Telegram), SDK tetap
 * ada tapi initData kosong. UI harus tetap bekerja untuk dev/debug.
 */
/**
 * Tombol chrome Telegram ("Tutup", menu, chevron) MELAYANG di atas webview,
 * dan `env(safe-area-inset-top)` tidak menghitungnya — akibatnya header app
 * tertimpa. Telegram menyediakan ukurannya lewat `contentSafeAreaInset`
 * (Bot API 8.0+). Nilainya kita tulis sebagai CSS variable supaya bisa
 * dipakai util `.safe-top` / `.safe-bottom`.
 *
 * Bila klien terlalu lama untuk menyediakannya, kita pasang cadangan tetap
 * agar header tidak pernah tertimpa — lebih baik sedikit berlebih daripada
 * tombol tidak bisa ditekan.
 */
const MIN_TOP_IN_TELEGRAM_PX = 84;

function applyInsets(): void {
  try {
    const wa = WebApp as unknown as {
      initData?: string;
      safeAreaInset?: { top?: number; bottom?: number };
      contentSafeAreaInset?: { top?: number; bottom?: number };
      onEvent?: (e: string, cb: () => void) => void;
    };
    const root = document.documentElement;
    const inTelegram = (wa.initData ?? '').length > 0;

    const sync = () => {
      const sa = wa.safeAreaInset ?? {};
      const ca = wa.contentSafeAreaInset ?? {};

      if (typeof sa.top === 'number') {
        root.style.setProperty('--tg-safe-top', `${sa.top}px`);
      }
      if (typeof sa.bottom === 'number') {
        root.style.setProperty('--tg-safe-bottom', `${sa.bottom}px`);
      }

      if (typeof ca.top === 'number' && ca.top > 0) {
        // Klien tahu persis berapa tinggi chrome-nya → percayai.
        root.style.setProperty('--tg-content-top', `${ca.top}px`);
        root.style.removeProperty('--tg-min-top');
      } else if (inTelegram) {
        // Klien lama, atau melaporkan 0 padahal tombolnya tetap melayang.
        // Pasang ambang minimum supaya header tidak pernah tertimpa.
        root.style.setProperty('--tg-content-top', '0px');
        root.style.setProperty('--tg-min-top', `${MIN_TOP_IN_TELEGRAM_PX}px`);
      }
      if (typeof ca.bottom === 'number') {
        root.style.setProperty('--tg-content-bottom', `${ca.bottom}px`);
      }
    };

    sync();
    wa.onEvent?.('safeAreaChanged', sync);
    wa.onEvent?.('contentSafeAreaChanged', sync);
    wa.onEvent?.('viewportChanged', sync);
  } catch {
    // Browser biasa — biarkan env() yang bekerja.
  }
}

export const telegram = {
  /** `initData` string mentah untuk dikirim ke backend `Authorization: tma <initData>`. */
  get initData(): string {
    return WebApp.initData ?? '';
  },

  /** User yang sedang login (bila ada). */
  get user() {
    return WebApp.initDataUnsafe?.user ?? null;
  },

  get platform(): string {
    return WebApp.platform ?? 'unknown';
  },

  get colorScheme(): 'light' | 'dark' {
    return WebApp.colorScheme ?? 'dark';
  },

  /** Panggil sekali di startup. */
  ready(): void {
    try {
      WebApp.ready();
      WebApp.expand();
      /*
       * Matikan gestur "geser ke bawah untuk menutup" milik Telegram.
       * Di iOS, geseran mendatar yang sedikit miring sering terbaca
       * sebagai gestur itu, sehingga Mini App mulai menutup di tengah
       * orang menggeser carousel. Tersedia sejak Bot API 7.7; versi lama
       * mengabaikannya (karena itu dibungkus optional call).
       */
      (
        WebApp as unknown as { disableVerticalSwipes?: () => void }
      ).disableVerticalSwipes?.();
    } catch {
      // Bila dibuka di browser biasa, panggilan ini bisa no-op / throw.
    }
    applyInsets();
  },

  /**
   * Samakan warna header & latar Telegram dengan tema pilihan user
   * (lihat lib/theme.ts). Tanpa ini, tema terang di app tetap berbatasan
   * dengan header hitam milik Telegram.
   */
  setChromeColor(hex: string): void {
    try {
      WebApp.setHeaderColor(hex as `#${string}`);
      WebApp.setBackgroundColor(hex as `#${string}`);
    } catch {
      // Bot API lawas hanya menerima keyword, atau kita di browser biasa.
    }
  },

  /** Tampilkan alert native Telegram (fallback ke window.alert). */
  showAlert(message: string): void {
    if (WebApp.initData) {
      WebApp.showAlert(message);
    } else {
      // eslint-disable-next-line no-alert
      window.alert(message);
    }
  },

  /**
   * Tombol back NATIVE Telegram (chevron di header + tombol back sistem
   * Android). Semua di-guard try/catch supaya no-op di browser biasa.
   */
  showBackButton(): void {
    try {
      WebApp.BackButton.show();
    } catch {
      /* browser biasa */
    }
  },
  hideBackButton(): void {
    try {
      WebApp.BackButton.hide();
    } catch {
      /* browser biasa */
    }
  },
  onBackButton(handler: () => void): void {
    try {
      WebApp.BackButton.onClick(handler);
    } catch {
      /* browser biasa */
    }
  },
  offBackButton(handler: () => void): void {
    try {
      WebApp.BackButton.offClick(handler);
    } catch {
      /* browser biasa */
    }
  },

  /**
   * Mode layar penuh milik Telegram (Bot API 8.0+).
   *
   * Berbeda dari Fullscreen API browser: yang dijadikan layar penuh adalah
   * SELURUH mini app (chrome Telegram disembunyikan), bukan elemen video.
   * Jadi pemanggilnya masih harus membesarkan videonya sendiri lewat CSS.
   *
   * Dipakai karena WebView Telegram di Android kerap menolak
   * requestFullscreen biasa.
   */
  get supportsFullscreen(): boolean {
    try {
      const w = WebApp as unknown as { requestFullscreen?: () => void };
      return (
        typeof w.requestFullscreen === 'function' && WebApp.initData.length > 0
      );
    } catch {
      return false;
    }
  },

  get isFullscreen(): boolean {
    try {
      return Boolean((WebApp as unknown as { isFullscreen?: boolean }).isFullscreen);
    } catch {
      return false;
    }
  },

  requestFullscreen(): void {
    try {
      (WebApp as unknown as { requestFullscreen?: () => void }).requestFullscreen?.();
    } catch {
      /* klien lama */
    }
  },

  exitFullscreen(): void {
    try {
      (WebApp as unknown as { exitFullscreen?: () => void }).exitFullscreen?.();
    } catch {
      /* klien lama */
    }
  },

  /** Daftarkan/lepas listener event Telegram (fullscreenChanged, dll). */
  onEvent(name: string, cb: () => void): void {
    try {
      (
        WebApp as unknown as { onEvent?: (n: string, c: () => void) => void }
      ).onEvent?.(name, cb);
    } catch {
      /* browser biasa */
    }
  },
  offEvent(name: string, cb: () => void): void {
    try {
      (
        WebApp as unknown as { offEvent?: (n: string, c: () => void) => void }
      ).offEvent?.(name, cb);
    } catch {
      /* browser biasa */
    }
  },

  /**
   * Penguncian orientasi milik Telegram (Bot API 8.0+). Mengunci ke
   * orientasi yang SEDANG dipakai — dipakai saat menonton supaya layar
   * berhenti berputar sendiri mengikuti gerakan tangan.
   */
  lockOrientation(): void {
    try {
      (WebApp as unknown as { lockOrientation?: () => void }).lockOrientation?.();
    } catch {
      /* klien lama / browser biasa */
    }
  },
  unlockOrientation(): void {
    try {
      (
        WebApp as unknown as { unlockOrientation?: () => void }
      ).unlockOrientation?.();
    } catch {
      /* klien lama / browser biasa */
    }
  },

  /** Getar (haptic feedback) — no-op di browser biasa. */
  haptic(style: 'light' | 'medium' | 'heavy' = 'light'): void {
    try {
      WebApp.HapticFeedback.impactOccurred(style);
    } catch {
      /* browser biasa */
    }
  },

  /** Apakah kita sedang di dalam Telegram (bukan browser). */
  get isInTelegram(): boolean {
    return WebApp.initData.length > 0;
  },
};
